/**
 * Los temas — de qué se escribe, guardados, y cuáles van primero.
 *
 * Desde M9 los temas PERSISTEN (Eduardo, 2026-09-06: *"los tópicos deberían
 * persistir y poder marcar cuáles quiero que se escriban"*). La revisión
 * semanal los busca y guarda; Evo Brain marca sus cinco mejores; Eduardo marca
 * los suyos, y los suyos van primero cuando la revisión escribe. Descartar
 * uno lo saca de la lista sin borrarlo; usado es el que ya se escribió.
 *
 * La fuente se ve de un vistazo porque es lo que separa demanda medida de
 * criterio:
 *   · **busqueda** — lo que la gente escribe en Google (autocompletar).
 *   · **gsc** — una consulta por la que el sitio YA aparece.
 *   · **web** — lo que el rubro discute ahora, con fuente.
 *   · **sitio** — un hueco de lo que la página cubre.
 *   · **nicho** — criterio, no evidencia. El más débil, en tono apagado.
 *
 * Sin nicho no hay temas, y eso se dice con el botón para resolverlo acá mismo.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailSection } from '../../ui/DetailSection.js';
import { SectionHeaderAction } from '../../ui/SectionHeaderAction.js';
import { Button } from '../../ui/Button.js';
import { LoadingState } from '../../ui/LoadingState.js';
import { Input } from '../../ui/Input.js';
import { Checkbox } from '../../ui/Checkbox.js';
import { ConfirmDialog } from '../../ui/ConfirmDialog.js';
import { Pill } from '../../PillGroup.js';
import type { PillTone } from '../../PillGroup.js';
import { useToast } from '../../ui/Toast.js';
import type { FuenteDelTema, TemaGuardado } from '../../../hooks/useMantenimiento.js';
import {
  useMantenimiento, useNicho, useFijarNicho, useTemas, useBuscarTemas, useMarcarTema, useEscribirArticulo,
} from '../../../hooks/useMantenimiento.js';

/** El tono dice cuánto pesa la fuente. Criterio no se pinta como dato medido. */
const TONO: Record<FuenteDelTema, PillTone> = { busqueda: 'cyan', web: 'cyan', gsc: 'cyan', sitio: 'blue', nicho: 'muted' };
const ETIQUETA: Record<FuenteDelTema, string> = {
  busqueda: 'projects.maintenance.topics.sourceBusqueda',
  web: 'projects.maintenance.topics.sourceWeb',
  gsc: 'projects.maintenance.topics.sourceGsc',
  sitio: 'projects.maintenance.topics.sourceSitio',
  nicho: 'projects.maintenance.topics.sourceNicho',
};

const menor = {
  fontSize: 'var(--evo-fs-tiny)',
  color: 'var(--evo-fg-4)',
  fontFamily: 'var(--evo-font)',
} as const;

export function SeccionTemas({ projectId }: { projectId: number }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: estado } = useMantenimiento(projectId);
  const encendido = estado?.enabled === true;
  const { data: nichoData } = useNicho(projectId, encendido);
  const fijarNicho = useFijarNicho(projectId);
  const { data: lista } = useTemas(projectId, encendido, true);
  const buscar = useBuscarTemas(projectId);
  const marcar = useMarcarTema(projectId);
  const escribir = useEscribirArticulo(projectId);
  const [texto, setTexto] = useState('');
  const [verCerrados, setVerCerrados] = useState(false);
  // El tema sobre el que se preguntó "¿todos o solo este?". Se guarda el tema,
  // no un booleano: si contesta "solo este" hay que saber cuál era.
  const [aConfirmar, setAConfirmar] = useState<TemaGuardado | null>(null);
  const [lote, setLote] = useState<{ hecho: number; total: number; titulo: string } | null>(null);
  // El resultado NO se va solo. Un toast de seis segundos para un proceso de
  // varios minutos se pierde: Eduardo se entera de que algo pasó mirando la
  // lista, no el aviso. Se queda hasta que lo cierre.
  const [cierre, setCierre] = useState<{ hechos: number; total: number; error: string | null } | null>(null);

  if (!encendido) return null;
  const nicho = nichoData?.nicho ?? null;
  const fallo = (e: unknown): void => { toast.error(e instanceof Error ? e.message : t('common.error')); };

  const todos = lista?.temas ?? [];
  const abiertos = todos.filter(x => x.estado === 'propuesto' || x.estado === 'marcado');
  const cerrados = todos.filter(x => x.estado === 'descartado' || x.estado === 'usado');
  // Ya vienen ordenados por el servidor (temasGuardados.ts): los tuyos primero,
  // después los de Evo Brain. Escribir en lote respeta ESE orden y no inventa
  // otro — el mismo que usa la revisión semanal.
  const listaMarcados = abiertos.filter(x => x.estado === 'marcado');
  const marcados = listaMarcados.length;
  const ocupado = marcar.isPending || escribir.isPending || lote !== null;

  /**
   * Uno detrás de otro, no en paralelo: cada artículo es una redacción, una
   * portada, las traducciones y un aviso. Lanzarlos juntos multiplica el gasto
   * y deja el sitio a medio escribir si algo falla en el medio.
   *
   * Si uno falla se corta ahí y se dice cuántos quedaron escritos: los que ya
   * salieron están guardados y su tema quedó marcado como usado, así que
   * reintentar NO los vuelve a escribir.
   */
  const escribirEnSerie = async (temas: TemaGuardado[]): Promise<void> => {
    if (temas.length === 0) return;
    setCierre(null);
    let hechos = 0;
    let primerError: string | null = null;
    try {
      for (const tema of temas) {
        setLote({ hecho: hechos, total: temas.length, titulo: tema.titulo });
        try {
          await escribir.mutateAsync(tema);
          hechos += 1;
        } catch (e) {
          // Uno que falla NO cancela los que siguen. Antes sí, y el 2026-09-17
          // un 'sin-respuesta' en el segundo artículo dejó el tercero sin
          // escribir sin que nada lo hubiera intentado. Los temas escritos
          // quedan 'usado', así que volver a darle solo toma los que faltan.
          primerError ??= e instanceof Error ? e.message : t('common.error');
        }
      }
      setCierre({ hechos, total: temas.length, error: primerError });
      if (primerError === null) toast.success(t('projects.maintenance.topics.batchDone', { count: hechos }));
      else toast.error(t('projects.maintenance.topics.batchStopped', { hechos, total: temas.length, error: primerError }));
    } finally {
      setLote(null);
    }
  };

  // Habiendo dos o más marcados, "escribir" es ambiguo y la ambigüedad la
  // resuelve Eduardo, no un valor por defecto.
  //
  // La primera versión solo preguntaba si el tema PULSADO estaba marcado. Era
  // de más: con tres marcados y un clic en uno sin marcar, escribía ese, en
  // silencio, y desde afuera se veía idéntico a que el lote no existiera
  // (Eduardo, 2026-09-17: *"me sigue solo creando un artículo"*). Ahora la
  // pregunta depende solo de cuántos hay marcados.
  const pedirEscribir = (tema: TemaGuardado): void => {
    if (marcados > 1) { setAConfirmar(tema); return; }
    escribir.mutate(tema, { onError: fallo });
  };
  // Las palabras de la última revisión: lo que la gente escribe en Google
  // sobre el nicho. Es de dónde salen los temas que rankean.
  const palabras = estado?.revision.ultima?.resumen?.palabras ?? buscar.data?.evidencia.palabras.map(w => w.consulta) ?? [];

  // Cerrada por defecto: la lista de temas es larga y la trabaja Evo Brain;
  // el total y lo marcado se leen en la cabecera. Abre sola solo si falta el
  // nicho, que es lo único que tiene que hacer Eduardo para que haya temas.
  // El nicho llega en otra consulta: la tarjeta se monta de nuevo al llegar
  // para que esa decisión se tome con el dato y no con el vacío de la carga.
  return (
    <DetailSection
      key={nichoData ? 'temas' : 'temas-cargando'}
      title={t('projects.maintenance.topics.title')}
      count={abiertos.length}
      padding="none"
      collapsible
      defaultOpen={nichoData !== undefined && !nicho}
      storageKey={`temas:${String(projectId)}`}
      titleRight={
        <>
          {abiertos.length > 0 && (
            <span style={{ ...menor, whiteSpace: 'nowrap' }}>
              {lote
                ? t('projects.maintenance.topics.batchProgress', { n: lote.hecho + 1, total: lote.total })
                : t('projects.maintenance.topics.markedCount', { marcados, total: abiertos.length })}
            </span>
          )}
          {/* El camino explícito para escribir el lote. Depender solo del botón
              de una fila obliga a saber que pulsar ahí significa "todos", que
              es exactamente lo que no se entendía. */}
          {marcados > 0 && (
            <SectionHeaderAction
              onClick={() => { void escribirEnSerie(listaMarcados); }}
              loading={lote !== null}
              disabled={ocupado}
            >
              {t('projects.maintenance.topics.writeMarked', { count: marcados })}
            </SectionHeaderAction>
          )}
          {nicho && (
            <SectionHeaderAction onClick={() => { buscar.mutate(undefined, { onError: fallo }); }} loading={buscar.isPending}>
              {t('projects.maintenance.topics.search')}
            </SectionHeaderAction>
          )}
        </>
      }
    >
      <div style={{ padding: 'var(--evo-space-3) var(--evo-space-4)', borderBottom: '1px solid var(--evo-stroke)', background: 'var(--evo-panel)' }}>
        {nicho
          ? (
            <div style={menor}>
              {t('projects.maintenance.topics.niche')}: <span style={{ color: 'var(--evo-fg-2)' }}>{nicho.texto}</span>
              {' · '}{t('projects.maintenance.topics.listHint')}
            </div>
          )
          : (
            <div>
              <p style={{ margin: '0 0 var(--evo-space-2)', fontSize: 'var(--evo-fs-body)', color: 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)', maxWidth: '62ch' }}>
                {t('projects.maintenance.topics.nicheMissing')}
              </p>
              <div style={{ display: 'flex', gap: 'var(--evo-space-2)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Input value={texto} onChange={setTexto} size="sm" fullWidth placeholder={t('projects.maintenance.topics.nichePlaceholder')} />
                <Button size="sm" tone="cyan" disabled={!texto.trim()} loading={fijarNicho.isPending} onClick={() => { fijarNicho.mutate(texto.trim()); }}>
                  {t('projects.maintenance.topics.nicheSave')}
                </Button>
                {/* Deducirlo puede salir mal, y por eso el servidor guarda de
                    quién salió: una inferencia errada sin esa marca se vuelve
                    la base de meses de contenido. */}
                <Button size="sm" variant="outline" loading={fijarNicho.isPending} onClick={() => { fijarNicho.mutate(undefined); }}>
                  {t('projects.maintenance.topics.nicheDeduce')}
                </Button>
              </div>
            </div>
          )}
      </div>

      {palabras.length > 0 && (
        <div style={{ padding: 'var(--evo-space-3) var(--evo-space-4)', borderBottom: '1px solid var(--evo-stroke)' }}>
          <div style={{ ...menor, textTransform: 'uppercase', letterSpacing: 'var(--evo-ls-caps)', marginBottom: 'var(--evo-space-2)' }}>
            {t('projects.maintenance.topics.keywords')}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--evo-space-2)' }}>
            {palabras.slice(0, 20).map(w => <Pill key={w} tone="soft" variant="subtle" size="xs">{w}</Pill>)}
          </div>
          <p style={{ ...menor, margin: 'var(--evo-space-2) 0 0', maxWidth: '76ch' }}>{t('projects.maintenance.topics.keywordsHint')}</p>
        </div>
      )}

      {buscar.data && [
        buscar.data.evidencia.sinPalabrasPorque ? t('projects.maintenance.topics.noKeywords', { porque: buscar.data.evidencia.sinPalabrasPorque }) : null,
        buscar.data.evidencia.sinConsultasPorque ? t('projects.maintenance.topics.noQueries', { porque: buscar.data.evidencia.sinConsultasPorque }) : null,
        buscar.data.evidencia.consultasDeMarca > 0 ? t('projects.maintenance.topics.brandExcluded', { count: buscar.data.evidencia.consultasDeMarca }) : null,
        buscar.data.evidencia.sinTendenciasPorque ? t('projects.maintenance.topics.noTrends', { porque: buscar.data.evidencia.sinTendenciasPorque }) : null,
      ].filter((x): x is string => x !== null).map(linea => (
        <p key={linea} style={{ ...menor, margin: 0, padding: 'var(--evo-space-2) var(--evo-space-4)', borderBottom: '1px solid var(--evo-stroke)', color: 'var(--evo-amber)', maxWidth: '76ch' }}>
          {linea}
        </p>
      ))}

      {(lote !== null || cierre !== null) && (
        <div style={{
          padding: 'var(--evo-space-3) var(--evo-space-4)',
          borderBottom: '1px solid var(--evo-stroke)',
          background: 'var(--evo-panel)',
          display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-3)',
        }}>
          {lote !== null
            ? (
              <>
                <LoadingState size="sm" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)' }}>
                    {t('projects.maintenance.topics.batchWriting', { n: lote.hecho + 1, total: lote.total })}
                    {' · '}
                    <span style={{ color: 'var(--evo-fg-3)' }}>{lote.titulo}</span>
                  </div>
                  <div style={{ ...menor, marginTop: '2px' }}>{t('projects.maintenance.topics.batchEach')}</div>
                  {/* El lote vive en esta pestaña. Decirlo acá y no en el modal:
                      el modal ya se cerró cuando esto importa. */}
                  <div style={{ ...menor, marginTop: '2px', color: 'var(--evo-amber)' }}>
                    {t('projects.maintenance.topics.batchKeepOpen')}
                  </div>
                </div>
              </>
            )
            : cierre !== null && (
              <>
                <Pill tone={cierre.error ? 'warn' : 'success'} variant="outline" size="sm">
                  {cierre.hechos}/{cierre.total}
                </Pill>
                <div style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)' }}>
                  {cierre.error
                    ? t('projects.maintenance.topics.batchFailBanner', { hechos: cierre.hechos, total: cierre.total, error: cierre.error })
                    : t('projects.maintenance.topics.batchOkBanner', { count: cierre.hechos })}
                </div>
                <Button size="xs" variant="link" tone="muted" onClick={() => { setCierre(null); }}>
                  {t('projects.maintenance.topics.batchDismiss')}
                </Button>
              </>
            )}
        </div>
      )}

      <div style={{ padding: '0 var(--evo-space-4)' }}>
        {abiertos.length > 0
          ? abiertos.map(tema => (
            <FilaTema
              key={tema.id}
              tema={tema}
              ocupado={ocupado}
              onMarcar={v => { marcar.mutate({ temaId: tema.id, estado: v ? 'marcado' : 'propuesto' }, { onError: fallo }); }}
              onDescartar={() => { marcar.mutate({ temaId: tema.id, estado: 'descartado' }, { onError: fallo }); }}
              onEscribir={() => { pedirEscribir(tema); }}
            />
          ))
          : (
            <p style={{ margin: 0, padding: 'var(--evo-space-3) 0', fontSize: 'var(--evo-fs-body)', color: 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)', maxWidth: '70ch' }}>
              {nicho ? t('projects.maintenance.topics.empty') : ''}
            </p>
          )}

        {cerrados.length > 0 && (
          <div style={{ padding: 'var(--evo-space-3) 0' }}>
            <Button size="xs" variant="outline" onClick={() => { setVerCerrados(v => !v); }}>
              {t('projects.maintenance.topics.closedCount', { count: cerrados.length })} {verCerrados ? '▴' : '▾'}
            </Button>
            {verCerrados && cerrados.map(tema => (
              <div key={tema.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', padding: 'var(--evo-space-2) 0', borderBottom: '1px solid var(--evo-stroke)' }}>
                <Pill tone="muted" variant="subtle" size="xs">
                  {t(tema.estado === 'usado' ? 'projects.maintenance.topics.used' : 'projects.maintenance.topics.discarded')}
                </Pill>
                <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>{tema.titulo}</span>
                {tema.estado === 'descartado' && (
                  <Button size="xs" variant="link" tone="muted" loading={marcar.isPending} onClick={() => { marcar.mutate({ temaId: tema.id, estado: 'propuesto' }, { onError: fallo }); }}>
                    {t('projects.maintenance.topics.restore')}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
        <div style={{ height: 'var(--evo-space-2)' }} />
      </div>

      <ConfirmDialog
        open={aConfirmar !== null}
        onClose={() => { setAConfirmar(null); }}
        tone="cyan"
        title={t('projects.maintenance.topics.batchTitle', { count: marcados })}
        description={t('projects.maintenance.topics.batchBody')}
        confirmLabel={t('projects.maintenance.topics.batchAll', { count: marcados })}
        onConfirm={() => {
          const temas = listaMarcados;
          setAConfirmar(null);
          void escribirEnSerie(temas);
        }}
        secondaryLabel={t('projects.maintenance.topics.batchOne')}
        onSecondary={() => {
          const tema = aConfirmar;
          setAConfirmar(null);
          if (tema) escribir.mutate(tema, { onError: fallo });
        }}
      />
    </DetailSection>
  );
}

/**
 * Un tema: el tilde de "escríbelo", la fuente, el porqué y la evidencia, y las
 * dos salidas — descartar, o escribirlo ya sin esperar a la revisión.
 */
function FilaTema({ tema, ocupado, onMarcar, onDescartar, onEscribir }: {
  tema: TemaGuardado;
  ocupado: boolean;
  onMarcar: (v: boolean) => void;
  onDescartar: () => void;
  onEscribir: () => void;
}) {
  const { t } = useTranslation();
  const marcado = tema.estado === 'marcado';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-3)', padding: 'var(--evo-space-3) 0', borderBottom: '1px solid var(--evo-stroke)' }}>
      <div style={{ paddingTop: '2px' }}>
        <Checkbox checked={marcado} onChange={onMarcar} disabled={ocupado} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)' }}>{tema.titulo}</span>
          <Pill tone={TONO[tema.fuente]} variant="subtle" size="xs">{t(ETIQUETA[tema.fuente])}</Pill>
          {marcado && (
            <Pill tone={tema.marcadoPor === 'eduardo' ? 'amber' : 'cyan'} variant="outline" size="xs">
              {t(tema.marcadoPor === 'eduardo' ? 'projects.maintenance.topics.markedByYou' : 'projects.maintenance.topics.markedByEvo')}
            </Pill>
          )}
        </div>
        {tema.porQue && <div style={{ ...menor, marginTop: '2px', maxWidth: '80ch' }}>{tema.porQue}</div>}
        {/* La evidencia concreta, aparte del porqué: es lo que permite
            distinguir "la gente busca esto" de "me pareció". */}
        {tema.evidencia && (
          <div style={{ ...menor, marginTop: '2px', maxWidth: '80ch', color: 'var(--evo-fg-3)' }}>
            {t('projects.maintenance.topics.evidence')}: {tema.evidencia}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)', alignItems: 'stretch', width: 'var(--evo-articulo-accion-w)' }}>
        <Button size="xs" variant="outline" tone="cyan" fullWidth loading={ocupado} onClick={onEscribir}>
          {t('projects.maintenance.topics.write')}
        </Button>
        <Button size="xs" variant="link" tone="muted" fullWidth disabled={ocupado} onClick={onDescartar}>
          {t('projects.maintenance.topics.discard')}
        </Button>
      </div>
    </div>
  );
}
